import { formatDate } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { CcdaCommunicationRequest } from '@seen/core/models/communicationRequests/CcdaCommunicationRequest'
import { saveBlobWithName } from '@seen/core/utils'
import {
  PageLayout,
  PractitionerDisplay,
  toast,
  useMedplum,
  useSearchOne,
} from '@seen/react'
import { Button } from '@seen/ui'
import { IconDownload, IconPrinter } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useParams } from 'react-router'
import { ccdaBinaryReference } from '#src/pages/ccda/ccdaBinaryReference.ts'
import { CcdaDisplay } from '#src/pages/ccda/components/CcdaDisplay.tsx'
import { usePrintCcda } from '#src/pages/ccda/hooks/usePrintCcda.ts'

export function CcdaViewerPage(): JSX.Element {
  const { communicationRequestId, patientId } = useParams<{
    communicationRequestId?: string
    patientId?: string
  }>()

  const medplum = useMedplum()

  let query
  if (communicationRequestId) {
    query = { _id: communicationRequestId }
  } else if (patientId) {
    query = CcdaCommunicationRequest.forPatient(patientId)
  }

  invariant(query, 'Either communicationRequestId or patientId is required')

  const { data: communicationRequest } = useSearchOne(
    CcdaCommunicationRequest,
    query
  )
  const attachment = communicationRequest?.ccdaAttachment
  const { printCcda, isPrinting } = usePrintCcda(attachment?.url)

  if (!communicationRequest) {
    return <div>CCDA export not found</div>
  }

  const handleDownload = async (): Promise<void> => {
    if (!attachment?.url) {
      return
    }

    try {
      const data = await medplum.download(ccdaBinaryReference(attachment.url))
      const xml = await data.text()
      saveBlobWithName(
        new Blob([xml], { type: 'application/cda+xml' }),
        `${attachment.title ?? 'ccda'}.xml`
      )
    } catch (error) {
      console.error('Error downloading CCDA:', error)
      toast.error('Could not download this CCDA')
    }
  }

  const handlePrintPdf = (): void => {
    if (!attachment) {
      return
    }
    void printCcda(attachment)
  }

  return (
    <PageLayout title="CCDA Viewer">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-sm text-muted-foreground">
              Exported:{' '}
              {communicationRequest.exportDate
                ? formatDate(communicationRequest.exportDate)
                : 'Unknown date'}
            </p>
            {communicationRequest.requester && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Requested by:
                </span>
                <PractitionerDisplay
                  practitioner={communicationRequest.requester}
                />
              </div>
            )}
            {attachment?.title && (
              <p className="text-sm font-medium">{attachment.title}</p>
            )}
          </div>
          {attachment && (
            <div className="flex gap-2">
              <Button size="sm" onClick={handlePrintPdf} disabled={isPrinting}>
                <IconPrinter size={16} className="mr-2" />
                {isPrinting ? 'Preparing PDF...' : 'Print PDF'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <IconDownload size={16} className="mr-2" />
                Download XML
              </Button>
            </div>
          )}
        </div>

        {attachment ? (
          <CcdaDisplay attachment={attachment} maxWidth={1200} height={800} />
        ) : (
          <p className="text-destructive">
            No CCDA document attached to this export
          </p>
        )}
      </div>
    </PageLayout>
  )
}
